/**
 * Earthwise - "Find My Zone" popup
 *
 * Lets a shopper enter a ZIP code, shows the matching Earthwise climate zone,
 * and on "Save Location" selects that zone in the hidden #zone-selector-dropdown
 * so assets/zone-filter.js can filter collection pages site-wide.
 *
 * Markup: snippets/zone-finder.liquid   (popup + hidden dropdown)
 *         sections/header.liquid        (#zone-finder-btn, desktop)
 *         snippets/header-drawer.liquid (#zone-finder-btn-mobile, drawer)
 * Data:   assets/zip-climate-regions.js (sets window.zipToClimateRegion). It is
 *         about 550 KB, so it is only fetched the first time the popup is used.
 *         The URL comes from the popup's data-zip-src attribute.
 * Load order (deferred): zone-finder.js, then zone-filter.js
 */
(function () {
  'use strict';

  const STORAGE_KEY_ZIP = 'selectedZipCode';
  const STORAGE_KEY_GID = 'selectedClimateZoneGID'; // written by zone-filter.js
  const STORAGE_KEY_NAME = 'selectedClimateZoneName'; // written by zone-filter.js

  const modal = document.getElementById('zone-modal');
  const dropdown = document.getElementById('zone-selector-dropdown');
  if (!modal || !dropdown) return;

  // The popup is rendered inside the header section, whose stacking order sits
  // below the announcement bar. Moving it to <body> puts it above everything.
  document.body.appendChild(modal);

  const content = modal.querySelector('.zone-modal__content');
  const openBtn = document.getElementById('zone-finder-btn');
  const openBtnMobile = document.getElementById('zone-finder-btn-mobile');
  const closeBtn = document.getElementById('zone-modal-close');
  const overlay = modal.querySelector('.zone-modal__overlay');
  const zipInput = document.getElementById('zip-input');
  const resultDiv = document.getElementById('zone-result');
  const resultText = document.getElementById('zone-result-text');
  const errorDiv = document.getElementById('zone-error');
  const loadingEl = document.getElementById('zone-loading');
  const savedEl = document.getElementById('zone-saved');
  const statusEl = document.getElementById('zone-status');
  const saveBtn = document.getElementById('save-zone-btn');
  const clearModalBtn = document.getElementById('clear-zone-modal-btn');
  const buttonInfo = openBtn ? openBtn.querySelector('.zone-selector__button-info') : null;
  const buttonInfoMobile = openBtnMobile
    ? openBtnMobile.querySelector('.zone-selector__button-info-mobile')
    : null;

  // Region code from the ZIP database -> zone metaobject handle
  const regionToZoneHandle = {
    '1w': 'zone-1-w',
    '1e': 'zone-1-e',
    '2w': 'zone-2-w',
    '2e': 'zone-2-e',
    '3w': 'zone-3-w',
    '3e': 'zone-3-e',
    4: 'zone-4',
    5: 'zone-5',
    6: 'zone-6',
    7: 'zone-7',
  };

  // Zone that was looked up but not yet saved (applied only on "Save Location")
  let pendingZone = null;
  // Element that had focus before the popup opened
  let lastFocused = null;
  // Pending fetch of the ZIP table
  let zipDataPromise = null;

  function readStorage(key) {
    try {
      return localStorage.getItem(key) || '';
    } catch (e) {
      return '';
    }
  }

  function removeStorage(keys) {
    try {
      keys.forEach(function (key) {
        localStorage.removeItem(key);
      });
    } catch (e) {
      console.warn('localStorage not available:', e);
    }
  }

  // A saved zone that no longer matches any zone in the list (for example after the
  // metaobject entries were recreated) would make zone-filter.js add a dead filter to
  // every collection URL, so forget it. Runs before zone-filter.js initialises.
  (function dropStaleSavedZone() {
    const savedGid = readStorage(STORAGE_KEY_GID);
    if (!savedGid) return;
    const known = Array.prototype.some.call(dropdown.options, function (option) {
      return option.value === savedGid;
    });
    if (!known) removeStorage([STORAGE_KEY_GID, STORAGE_KEY_NAME, STORAGE_KEY_ZIP]);
  })();

  // "Zone 1E" from a handle like "zone-1-e" or a name like "Zone 1E - Cold & Humid"
  function formatZoneName(zoneName, zoneHandle) {
    zoneName = zoneName || '';
    zoneHandle = zoneHandle || '';
    if (!zoneName && !zoneHandle) return '';

    const handleMatch = zoneHandle.match(/zone-(\d+)-?([a-z])?/i);
    if (handleMatch) {
      const letter = handleMatch[2] ? handleMatch[2].toUpperCase() : '';
      return 'Zone ' + handleMatch[1] + letter;
    }

    const zoneMatch = zoneName.match(/Zone\s*(\d+[A-Z]?)/i);
    if (zoneMatch) return 'Zone ' + zoneMatch[1].toUpperCase();

    const climateMatch = zoneName.match(/Climate\s*Region\s*(\d+[A-Z]?)/i);
    if (climateMatch) return 'Zone ' + climateMatch[1].toUpperCase();

    const numberMatch = zoneName.match(/(\d+[A-Z]?)/i);
    if (numberMatch) return 'Zone ' + numberMatch[1].toUpperCase();

    return zoneName;
  }

  function saveZipCode(zip) {
    try {
      if (zip) {
        localStorage.setItem(STORAGE_KEY_ZIP, zip);
      } else {
        localStorage.removeItem(STORAGE_KEY_ZIP);
      }
    } catch (e) {
      console.warn('localStorage not available:', e);
    }
  }

  // Header buttons show "Zone 1E • 12345" once saved, "Find My Zone" otherwise
  function updateButtonDisplay(zoneName, zipCode, zoneHandle) {
    const hasZone = Boolean(zoneName && zipCode);
    const zoneLabel = hasZone ? formatZoneName(zoneName, zoneHandle) : '';
    const label = hasZone ? zoneLabel + ' • ' + zipCode : '';
    const ariaLabel = hasZone ? 'Find My Zone, currently ' + zoneLabel + ' for ZIP ' + zipCode : 'Find My Zone';

    if (openBtn && buttonInfo) {
      openBtn.classList.toggle('has-zone', hasZone);
      openBtn.setAttribute('aria-label', ariaLabel);
      if (hasZone) {
        buttonInfo.textContent = label;
      } else {
        buttonInfo.innerHTML = '<span class="zone-selector__button-text">Find My Zone</span>';
      }
    }

    if (openBtnMobile && buttonInfoMobile) {
      openBtnMobile.classList.toggle('has-zone', hasZone);
      openBtnMobile.setAttribute('aria-label', ariaLabel);
      if (hasZone) {
        buttonInfoMobile.textContent = label;
      } else {
        buttonInfoMobile.innerHTML =
          '<span class="zone-selector__button-text-mobile">Find My Zone</span>';
      }
    }
  }

  // Screen-reader announcement (visually hidden live region)
  function setStatus(message) {
    if (statusEl) statusEl.textContent = message;
  }

  function setLoading(isLoading) {
    if (loadingEl) loadingEl.classList.toggle('active', isLoading);
  }

  // Line above the input showing the zone currently saved, if any
  function updateSavedZoneLine() {
    if (!savedEl) return;
    const savedZip = readStorage(STORAGE_KEY_ZIP);
    const selectedOption = dropdown.options[dropdown.selectedIndex];
    const hasZone = Boolean(savedZip && dropdown.value && selectedOption && selectedOption.dataset.zoneName);

    savedEl.textContent = '';
    if (hasZone) {
      const zone = document.createElement('strong');
      zone.textContent = formatZoneName(selectedOption.dataset.zoneName, selectedOption.dataset.zoneHandle);
      savedEl.appendChild(document.createTextNode('Your saved zone: '));
      savedEl.appendChild(zone);
      savedEl.appendChild(document.createTextNode(' (' + savedZip + ')'));
    }
    savedEl.classList.toggle('active', hasZone);
  }

  function resetLookupState() {
    resultDiv.classList.remove('active');
    errorDiv.classList.remove('active');
    zipInput.removeAttribute('aria-invalid');
    setLoading(false);
    setStatus('');
    pendingZone = null;
    saveBtn.disabled = true;
  }

  function showError(message) {
    setLoading(false);
    errorDiv.textContent = message;
    errorDiv.classList.add('active');
    zipInput.setAttribute('aria-invalid', 'true');
    setStatus(message);
  }

  // Fetch the ZIP table once, on demand. The seed calculator page loads it itself,
  // in which case window.zipToClimateRegion already exists and nothing is fetched.
  function ensureZipData() {
    if (window.zipToClimateRegion) return Promise.resolve();
    if (!zipDataPromise) {
      zipDataPromise = new Promise(function (resolve, reject) {
        const src = modal.getAttribute('data-zip-src');
        if (!src) {
          reject(new Error('ZIP data URL missing'));
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = function () {
          zipDataPromise = null;
          reject(new Error('ZIP data failed to load'));
        };
        document.head.appendChild(script);
      });
    }
    return zipDataPromise;
  }

  function openModal() {
    lastFocused = document.activeElement;
    modal.classList.add('active');
    zipInput.value = '';
    resetLookupState();
    updateSavedZoneLine();
    if (typeof trapFocus === 'function') {
      trapFocus(content, zipInput); // Dawn helper in global.js: keeps Tab inside the popup
    } else {
      zipInput.focus();
    }
    ensureZipData().catch(function () {}); // start the download early; errors surface on lookup
  }

  function closeModal() {
    if (!modal.classList.contains('active')) return;
    modal.classList.remove('active');
    pendingZone = null;
    saveBtn.disabled = true;

    const returnTo = lastFocused && document.contains(lastFocused) ? lastFocused : null;
    lastFocused = null;
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(returnTo);
    } else if (returnTo && typeof returnTo.focus === 'function') {
      returnTo.focus();
    }
  }

  function lookupZone(zipCode) {
    resetLookupState();

    if (!zipCode || zipCode.length !== 5) {
      showError('Please enter a valid 5-digit ZIP code.');
      return;
    }

    if (!window.zipToClimateRegion) {
      setLoading(true);
      setStatus('Loading ZIP code data');
      ensureZipData()
        .then(function () {
          if (!window.zipToClimateRegion) throw new Error('ZIP data missing');
          // Only continue if the shopper has not changed the ZIP meanwhile
          if (zipInput.value.trim() === zipCode) lookupZone(zipCode);
        })
        .catch(function () {
          showError('ZIP code lookup is unavailable right now. Please try again.');
        });
      return;
    }

    const region = window.zipToClimateRegion[zipCode];
    if (!region) {
      showError('ZIP code not found. Please check your ZIP code and try again.');
      return;
    }

    const normalizedRegion = String(region).toLowerCase().trim();
    const expectedHandle = regionToZoneHandle[normalizedRegion];
    if (!expectedHandle) {
      showError('Unknown region code: ' + region);
      return;
    }

    // Find the dropdown option for this zone (handle first, then name)
    let matchedOption = null;
    for (let i = 0; i < dropdown.options.length; i++) {
      const option = dropdown.options[i];
      const handle = (option.dataset.zoneHandle || '').toLowerCase();
      const zoneName = (option.dataset.zoneName || '').toLowerCase();

      if (
        handle === expectedHandle ||
        handle === normalizedRegion ||
        zoneName === 'zone ' + normalizedRegion ||
        zoneName.includes('zone ' + normalizedRegion)
      ) {
        matchedOption = option;
        break;
      }
    }

    if (!matchedOption) {
      showError('Zone found (' + region + ') but not available in dropdown.');
      return;
    }

    const formatted = formatZoneName(matchedOption.dataset.zoneName, matchedOption.dataset.zoneHandle);
    resultText.textContent = formatted;
    resultDiv.classList.add('active');
    setStatus('Your climate zone is ' + formatted + '. Press Save Location to use it.');
    pendingZone = { option: matchedOption, zipCode: zipCode };
    saveBtn.disabled = false;
  }

  // --- Events ---------------------------------------------------------------

  [openBtn, openBtnMobile].forEach(function (btn) {
    if (!btn) return;
    btn.addEventListener('click', function (event) {
      event.preventDefault();
      openModal();
    });
  });

  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  if (overlay) overlay.addEventListener('click', closeModal);

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && modal.classList.contains('active')) closeModal();
  });

  if (saveBtn) {
    saveBtn.addEventListener('click', function () {
      if (!pendingZone) return;

      saveZipCode(pendingZone.zipCode);

      // zone-filter.js listens for this change: it stores the zone and filters collections
      dropdown.value = pendingZone.option.value;
      dropdown.dispatchEvent(new Event('change', { bubbles: true }));

      updateButtonDisplay(
        pendingZone.option.dataset.zoneName,
        pendingZone.zipCode,
        pendingZone.option.dataset.zoneHandle
      );
      closeModal();
    });
  }

  if (clearModalBtn) {
    clearModalBtn.addEventListener('click', function () {
      dropdown.value = '';
      dropdown.dispatchEvent(new Event('change', { bubbles: true }));
      saveZipCode('');
      updateButtonDisplay('', '', '');
      closeModal();

      // Reload without the zone filter so a filtered collection grid resets too
      const url = new URL(window.location.href);
      url.searchParams.delete('filter.p.m.custom.zone');
      url.searchParams.delete('page');
      const hasOtherFilters = Array.from(url.searchParams.keys()).some(function (key) {
        return key.indexOf('filter.') === 0 && key !== 'filter.v.price.gte' && key !== 'filter.v.price.lte';
      });
      if (!hasOtherFilters) {
        url.searchParams.delete('filter.v.price.gte');
        url.searchParams.delete('filter.v.price.lte');
      }
      setTimeout(function () {
        window.location.href = url.toString();
      }, 100);
    });
  }

  if (zipInput) {
    zipInput.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        lookupZone(zipInput.value.trim());
      }
    });

    // Look up automatically once 5 digits are typed; forget the result if edited again
    zipInput.addEventListener('input', function (event) {
      const value = event.target.value.trim();
      if (value.length === 5) {
        lookupZone(value);
      } else {
        resetLookupState();
      }
    });
  }

  // Keep the header buttons in sync when zone-filter.js restores or changes the zone
  dropdown.addEventListener('change', function () {
    const savedZip = readStorage(STORAGE_KEY_ZIP);
    if (!dropdown.value || !savedZip) return;

    const selectedOption = dropdown.options[dropdown.selectedIndex];
    if (selectedOption && selectedOption.dataset.zoneName) {
      updateButtonDisplay(selectedOption.dataset.zoneName, savedZip, selectedOption.dataset.zoneHandle);
    }
  });

  // Initial button state. zone-filter.js runs next and restores a saved zone into the
  // dropdown, which fires the change listener above.
  const savedZip = readStorage(STORAGE_KEY_ZIP);
  const selectedOption = dropdown.options[dropdown.selectedIndex];
  if (savedZip && dropdown.value && selectedOption && selectedOption.dataset.zoneName) {
    updateButtonDisplay(selectedOption.dataset.zoneName, savedZip, selectedOption.dataset.zoneHandle);
  } else {
    updateButtonDisplay('', '', '');
  }
})();
